import {
  routePathIssues,
  type Depot,
  type RoutePath,
  type RoutePathCommandResult,
  type RoutePathStop,
  type RoutePathView,
} from '@groupe-reca/contracts'
import type { RouteRepository } from '../../application/RouteRepository'
import { RoutePathError, type RoutePathRepository } from '../../application/RoutePathRepository'
import { isLocated, routeMembers } from '../../domain/routePlanning'
import { recaDepot } from '../../domain/routeTools'

/**
 * Serveur simulé des parcours : mêmes règles que `route_paths_v1` (composition routée, empreinte, états, refus),
 * calculées sur les routes de démonstration. Aucune donnée réelle.
 */

const round6 = (value: number) => Math.round(value * 1e6) / 1e6

export function fingerprintOf(stops: readonly RoutePathStop[]): string {
  return stops
    .map((stop) => `${stop.serviceLocationId}@${String(stop.point[0])},${String(stop.point[1])}`)
    .join(';')
}

export function createFixtureRoutePaths(
  routes: Pick<RouteRepository, 'getPlanningSnapshot'>,
): RoutePathRepository {
  const paths: RoutePath[] = []
  const commands = new Map<string, RoutePathCommandResult>()
  const seasonOf = new Map<string, string>()

  async function composition(routeId: string, season: string) {
    const snapshot = await routes.getPlanningSnapshot(season)
    const route = snapshot.routes.find((candidate) => candidate.id === routeId)
    if (!route) throw new RoutePathError('ROUTE_PATH_ROUTE_NOT_FOUND')
    const members = routeMembers(snapshot, routeId)
    const stops: RoutePathStop[] = members.filter(isLocated).map((member, index) => {
      const flag = member.flagLatitude != null && member.flagLongitude != null
      const service: [number, number] = [round6(member.latitude ?? 0), round6(member.longitude ?? 0)]
      return {
        serviceLocationId: member.id,
        order: member.position ?? index + 1,
        point: flag ? [round6(member.flagLatitude ?? 0), round6(member.flagLongitude ?? 0)] : service,
        pointSource: flag ? 'FLAG' : 'SERVICE',
        service,
      }
    })
    return {
      route,
      stops,
      missing: members.filter((member) => !isLocated(member)).map((member) => member.id),
    }
  }

  async function view(routeId: string, season: string): Promise<RoutePathView> {
    seasonOf.set(routeId, season)
    const { route, stops, missing } = await composition(routeId, season)
    const fingerprint = fingerprintOf(stops)
    const now = Date.now()
    const draft =
      paths.filter((path) => path.routeId === routeId && path.status === 'CALCULATED').at(-1) ?? null
    const validated = paths.find((path) => path.routeId === routeId && path.status === 'VALIDATED') ?? null
    const fresh = (path: RoutePath | null) =>
      path !== null && path.fingerprint === fingerprint && Date.parse(path.expiresAt) > now
    const state =
      fresh(draft) && (!validated || (draft?.version ?? 0) > validated.version)
        ? 'TO_VALIDATE'
        : fresh(validated)
          ? 'VALIDATED'
          : validated || draft
            ? 'TO_RECALCULATE'
            : 'NOT_CALCULATED'
    return {
      routeId,
      routeVersion: route.version,
      state,
      fingerprint,
      stops,
      missingServiceLocationIds: missing,
      draft,
      validated,
    }
  }

  return {
    getView: view,
    async save(command) {
      const replay = commands.get(command.idempotencyKey)
      if (replay) return { ...replay, replayed: true }
      const season = seasonOf.get(command.routeId)
      if (!season) throw new RoutePathError('ROUTE_PATH_ROUTE_NOT_FOUND')
      const { route, stops } = await composition(command.routeId, season)
      if (route.version !== command.expectedRouteVersion) throw new RoutePathError('ROUTE_PATH_ROUTE_CHANGED')
      if (fingerprintOf(command.stops) !== fingerprintOf(stops))
        throw new RoutePathError('ROUTE_PATH_COMPOSITION_CHANGED')
      if (command.legs.length !== stops.length - 1) throw new RoutePathError('ROUTE_PATH_INCOMPLETE')
      if (routePathIssues(stops, command.waypoints, command.legs).length > 0)
        throw new RoutePathError('ROUTE_PATH_LEG_INVALID')
      for (const path of paths)
        if (path.routeId === command.routeId && path.status === 'CALCULATED') path.status = 'SUPERSEDED'
      const now = new Date()
      const path: RoutePath = {
        id: crypto.randomUUID(),
        routeId: command.routeId,
        version: paths.filter((candidate) => candidate.routeId === command.routeId).length + 1,
        status: 'CALCULATED',
        provider: command.provider,
        profile: command.profile,
        fingerprint: fingerprintOf(stops),
        stops,
        waypoints: command.waypoints,
        legs: command.legs,
        distanceMeters: command.legs.reduce((sum, leg) => sum + leg.distanceMeters, 0),
        durationSeconds: command.legs.reduce((sum, leg) => sum + leg.durationSeconds, 0),
        calculatedAt: now.toISOString(),
        calculatedByName: 'Démo Répartiteur',
        validatedAt: null,
        validatedByName: null,
        expiresAt: new Date(now.getTime() + 30 * 86_400_000).toISOString(),
      }
      paths.push(path)
      const result: RoutePathCommandResult = {
        pathId: path.id,
        version: path.version,
        status: path.status,
        replayed: false,
      }
      commands.set(command.idempotencyKey, result)
      return result
    },
    async validate(command) {
      const replay = commands.get(command.idempotencyKey)
      if (replay) return { ...replay, replayed: true }
      const path = paths.find((candidate) => candidate.id === command.pathId)
      if (!path) throw new RoutePathError('ROUTE_PATH_NOT_FOUND')
      if (path.status !== 'CALCULATED') throw new RoutePathError('ROUTE_PATH_NOT_VALIDATABLE')
      const current = await view(path.routeId, seasonOf.get(path.routeId) ?? '')
      if (current.fingerprint !== path.fingerprint) throw new RoutePathError('ROUTE_PATH_COMPOSITION_CHANGED')
      for (const other of paths)
        if (other.routeId === path.routeId && other.status === 'VALIDATED') other.status = 'SUPERSEDED'
      path.status = 'VALIDATED'
      path.validatedAt = new Date().toISOString()
      path.validatedByName = 'Démo Répartiteur'
      const result: RoutePathCommandResult = {
        pathId: path.id,
        version: path.version,
        status: path.status,
        replayed: false,
      }
      commands.set(command.idempotencyKey, result)
      return result
    },
    getDepot: (): Promise<Depot> =>
      Promise.resolve({ label: recaDepot.label, point: [recaDepot.lat, recaDepot.lng] }),
  }
}
